import clsx from "clsx"
import type { ButtonHTMLAttributes, ReactNode } from "react"
import { Link } from "react-router-dom"
import type { ButtonColor, ButtonVariant } from "@/shared/types"
import "./style.scss"
import type { IPermissionBase } from "@/entities/permission/types.ts"
import { Loader } from "@/shared/components/ui/icons"
import { WithPermissions } from "@/shared/ui/with-permissions.tsx"
import { cn } from "@/shared/utils"

export interface IButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  color?: ButtonColor
  size?: "small" | "medium" | "large"
  startIcon?: ReactNode
  endIcon?: ReactNode
  variant?: ButtonVariant
  to?: string
  loading?: boolean
}

export const Button = ({
  type = "button",
  color = "primary",
  size = "medium",
  startIcon,
  endIcon,
  variant = "contained",
  to,
  loading,
  ...props
}: IButtonProps) => {
  if (to != null)
    return (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      <Link {...props} to={to} className={clsx("button", `button-${variant}-${color}`, `button-size-${size}`, props.className)}>
        {startIcon}
        {props.children}
        {endIcon}
      </Link>
    )

  const isDisabled = props.disabled || loading

  return (
    <button
      {...props}
      className={cn(
        "button relative flex items-center ring-blue-400 focus:ring-2",
        `button-${variant}-${color}`,
        `button-size-${size}`,
        props.className,
        loading && "!bg-primary-hover"
      )}
      disabled={isDisabled}
      type={type}
    >
      {loading ? <Loader className="animate-spin" /> : startIcon}
      {props.children}
      {endIcon}
    </button>
  )
}

const ProtectedButton = ({ allowedPermissions, ...props }: IButtonProps & { allowedPermissions: IPermissionBase[] }) => {
  return (
    <WithPermissions allowedPermissions={allowedPermissions}>
      <Button {...props} />
    </WithPermissions>
  )
}

Button.Protected = ProtectedButton
